import type { ToastOptions } from "@/components/ui/toast"

/**
 * EVERY message the app can put in front of a reader, in one list.
 *
 * Before this, each message was defined wherever it happened to be raised, which meant nobody
 * could see the set. Listing them showed what that costs: of 23 distinct strings, 5 used a
 * contraction ("Couldn't…") and 2 spelled it out ("Could not…"); 12 ended in a full stop, 10 in
 * nothing, and 1 in an exclamation mark. `research.threadLoadFailed` and
 * `research.conversationLoadFailed` below are the same event worded two different ways in two
 * files — a duplicate that was invisible until the two sat on the same screen.
 *
 * THE WORDING HERE IS UNCHANGED from what each call site said. Normalising it is a copy pass,
 * and a copy pass wants one person reading the whole list and deciding the house style — not me
 * quietly rewriting 23 strings while moving them. The list is the deliverable; the edit is next.
 *
 * ── Static entries and factories ──────────────────────────────────────────────────────────
 *
 * Some messages are fixed. Others cannot be: the server supplies the whole string
 * (`projects.failed`), or the subject varies (`preferences.updateFailed`). Those are functions
 * rather than being left at the call site, so the catalogue still owns their SHAPE and their
 * fallback wording, and this file remains a complete account of what the app can say. A partial
 * list would be worse than none, because it would look complete.
 *
 * ── Tone ──────────────────────────────────────────────────────────────────────────────────
 *
 * Preserved exactly as each call site had it. Worth noting for the copy pass: the validation
 * messages ("Please enter a research question.") are `error`, because they were raised through
 * sonner's `toast.error`. They are arguably `warning` — nothing failed, the form is just
 * incomplete — but that is a behaviour change, so it is yours to make rather than mine.
 */

/** Identity. Exists only so each entry is checked against ToastOptions where it is written. */
const msg = (options: ToastOptions): ToastOptions => options

export const MESSAGES = {
  /* ── signing in and out ───────────────────────────────────────────────────────────────── */
  auth: {
    timedOut: msg({
      text: "Timed out!",
      description: "Your session has timed out. Please sign in again.",
      tone: "warning",
      dismiss: true,
    }),
    signedOut: msg({
      text: "Signed out!",
      description: "Your session has been succesfully closed out.",
      tone: "info",
      dismiss: true,
    }),
    accountDeleted: msg({
      text: "Account deleted!",
      description: "Your account has successfully been deleted.",
      tone: "info",
      dismiss: true,
    }),
    accountActivated: msg({
      text: "Account activated!",
      description: "Your account is now active. Please sign in to get started.",
      tone: "success",
      dismiss: true,
    }),
    /** Every refusal on the two auth forms, from a missing field to a server error. */
    problem: (description: string) =>
      msg({ text: "Problem!", description, tone: "error", dismiss: true }),
    missingEmail: "Please enter your email.",
    missingPassword: "Please enter your password.",
    termsNotAccepted: "You must accept the terms and conditions to continue.",
    unexpected: "An unexpected error occurred",
  },

  /* ── bookmarks ────────────────────────────────────────────────────────────────────────── */
  bookmark: {
    saved: msg({ text: "Bookmarked.", tone: "success" }),
    removed: msg({ text: "Bookmark removed.", tone: "success" }),
    signInRequired: msg({ text: "Sign in to bookmark.", tone: "warning" }),
    loadFailed: msg({ text: "Could not load bookmarks.", tone: "error" }),
    // The server's own reason wins where there is one: it says which constraint or column was
    // the problem, and the generic line says nothing the reader did not already infer.
    saveFailed: (reason?: string) =>
      msg({ text: reason ?? "Could not save bookmark.", tone: "error" }),
    removeFailed: (reason?: string) =>
      msg({ text: reason ?? "Could not remove bookmark.", tone: "error" }),
  },

  /* ── feedback ─────────────────────────────────────────────────────────────────────────── */
  feedback: {
    sent: msg({ text: "Thanks for helping us improve!", tone: "success" }),
    empty: msg({ text: "Enter some feedback first.", tone: "warning" }),
    signInRequired: msg({ text: "Sign in to send feedback.", tone: "warning" }),
    failed: msg({ text: "Could not send feedback.", tone: "error" }),
  },

  /* ── copying ──────────────────────────────────────────────────────────────────────────── */
  clipboard: {
    copied: msg({ text: "Copied to clipboard.", tone: "success" }),
    failed: msg({ text: "Failed to copy.", tone: "error" }),
  },

  /* ── research ─────────────────────────────────────────────────────────────────────────── */
  research: {
    rateLimited: msg({
      text: "Too many research jobs",
      description: "Please wait a moment — we'll keep checking.",
      tone: "error",
    }),
    tooManyInProgress: (reason?: string) =>
      msg({ text: "Too many research jobs already in progress", description: reason, tone: "error" }),
    jobFailed: (reason?: string) =>
      msg({ text: "Research job failed", description: reason, tone: "error" }),
    progressFailed: (reason?: string) =>
      msg({ text: "Couldn't update research progress", description: reason, tone: "error" }),
    submitFailed: (reason?: string) =>
      msg({ text: "Couldn't submit research request", description: reason, tone: "error" }),
    searchFailed: (reason?: string) =>
      msg({ text: "Couldn't run that search", description: reason, tone: "error" }),
    revisionSubmitted: msg({ text: "Revision submitted", tone: "success" }),

    // These two are the SAME EVENT, worded differently in two files — ThreadSheet said one and
    // useResearchJob the other. Left as they were for the copy pass to collapse; they are
    // adjacent here so the choice is visible.
    threadLoadFailed: (reason?: string) =>
      msg({ text: "Couldn't load conversation", description: reason, tone: "error" }),
    conversationLoadFailed: (reason?: string) =>
      msg({ text: "Couldn't load this conversation", description: reason, tone: "error" }),

    missingQuestion: msg({ text: "Please enter a research question.", tone: "error" }),
    missingClarification: msg({ text: "Please answer the clarifying question.", tone: "error" }),
    missingFollowUp: msg({ text: "Please enter a follow-up question.", tone: "error" }),
    missingRevision: msg({ text: "Please describe the revision you'd like.", tone: "error" }),
  },

  /* ── reading ──────────────────────────────────────────────────────────────────────────── */
  read: {
    noPropositionId: msg({
      text: "No proposition id",
      description:
        "This proposition isn't linked to the propositions index yet, so similar propositions can't be found.",
      tone: "info",
      dismiss: true,
    }),

    /* ── generating a document's AI analyses (EnrichButton) ──────────────────────────────── */

    // A factory: the service's own reason is worth more than a generic failure. A timeout and
    // a missing document are different things to be told, and only the server knows which.
    enrichFailed: (reason?: string) =>
      msg({
        text: "Analysis failed",
        description: reason ?? "Please try again.",
        tone: "error",
        dismiss: true,
      }),
    enrichEmpty: msg({
      text: "Nothing was generated",
      description: "The analysis returned no content for this document.",
      tone: "error",
      dismiss: true,
    }),
    enrichNoDocumentId: msg({
      text: "Can't analyse this document",
      description: "It has no document id.",
      tone: "error",
      dismiss: true,
    }),
  },

  /* ── search ───────────────────────────────────────────────────────────────────────────── */
  search: {
    missingQuery: msg({ text: "Please enter a search query.", tone: "error" }),
    // `execute_search` raised this same event as "Search Failed" — different capitalisation,
    // different punctuation, same failure. Collapsed onto the live wording rather than
    // catalogued twice; that one is a duplicate, not a choice.
    failed: (reason?: string) => msg({ text: "Search failed.", description: reason, tone: "error" }),
  },

  /* ── projects ─────────────────────────────────────────────────────────────────────────── */
  projects: {
    // The server writes these outright; the fallbacks are what shows when it does not.
    created: (message?: string) => msg({ text: message || "Project created", tone: "success" }),
    updated: (message?: string) => msg({ text: message || "Project updated", tone: "success" }),
    deleted: (message?: string) => msg({ text: message || "Project deleted", tone: "success" }),
    failed: (message: string) => msg({ text: message, tone: "error" }),
  },

  /* ── the account page ─────────────────────────────────────────────────────────────────── */
  account: {
    emailChangeSent: (message?: string) =>
      msg({
        text: message || "Verification email sent",
        description: "Please check your new email to confirm the change.",
        tone: "success",
      }),
    passwordUpdated: (message?: string) =>
      msg({ text: message || "Password updated", tone: "success" }),
    nameUpdated: msg({ text: "Name updated successfully", tone: "success" }),
    preferencesSaved: msg({ text: "Preferences saved", tone: "success" }),
    deleted: msg({ text: "Account deleted", tone: "success" }),
    /** Every account action reports its failure with the server's own reason. */
    failed: (message: string) => msg({ text: message, tone: "error" }),
  },

  /* ── preferences ──────────────────────────────────────────────────────────────────────── */
  preferences: {
    updateFailed: (label: string) =>
      msg({ text: `Failed to update ${label.toLowerCase()}`, tone: "error" }),
  },
} as const
